import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import { params, postJSON } from '../../../shared/util-fetch';
import { MediaQuery as MQ } from '../../../shared/media-query';
import _ from 'underscore';

// Helper function to get context
const getContext = () => {
    if (typeof window !== 'undefined' && window.location && window.location.href) {
        return window.location.href.includes('readings-reports') ? 'reading' : 'nav';
    }
    return 'nav';
};

// Helper function to get signup URL path
const getSignupUrl = () => {
    return typeof window !== 'undefined' && window.location ? window.location.pathname : '';
};

const getDevcat = () => {
    return typeof MQ.isMobile === 'function' && MQ.isMobile() ? 'Mobile' : 'Desktop';
};

/* eslint-disable complexity */
export function* signupApi({ payload }) {
    const devcat = getDevcat();
    const signupIntent = yield select((state) => state['signup-intent']);
    const ctx = (signupIntent && signupIntent.ctx) ? signupIntent.ctx : getContext();
    const meth = payload.signup_method || 'button';

    const api = yield select((state) => state.api);
    const extra_params = yield select((state) => {
        const promo = state.cookie && state.cookie.promo || {};
        const external = promo.signup_promo_external && promo.signup_promo_external.value;
        const internal = promo.signup_promo_internal && promo.signup_promo_internal.value;
        return {
            signup_promo_external: external || null,
            signup_promo_internal: internal || null,
            signup_property_id: state.property_id,
        };
    });

    yield put({
        type: 'user:update',
        payload: {
            submitting: true,
        },
    });

    try {
        yield call(
            postJSON,
            api.host + '/member/signup?' + params(api.params),
            { ...payload, ...extra_params }
        );

        // Calculate values for logging
        const signup_url = getSignupUrl();
        const dob_provided = payload.birth_date ? 'true' : 'false';
        const newsletter_opt_in = payload.subscribe_lists &&
            payload.subscribe_lists.includes('dailyhoroscope') ? 'true' : 'false';

        yield put({
            type: 'ga:custom_event',
            payload: {
                event_name: 'sign_up_success',
                params: {
                    devcat: devcat,
                    ctx: ctx,
                    meth: meth,
                    signup_url: signup_url,
                    dob_provided: dob_provided,
                    newsletter_opt_in: newsletter_opt_in,
                },
            },
        });

        yield put({ type: 'user:refresh' });

        const { payload: user } = yield take('user:update');

        yield put({ type: 'user:logged-in', payload: user });

        try {
            yield call(
                postJSON,
                '/api/event',
                {
                    event_type: 'signup',
                    event: {
                        url: signup_url,
                    },
                }
            );
        } catch (signupEventError) {
            // Do not block signup success if rules event dispatch fails.
        }

        yield put({
            type: 'ga:event',
            payload: [
                'sign_up',
                'Modal_Signup',
            ],
        });

        if (payload.birth_location) {
            yield take('user:update');

            yield put({
                type: 'member:update-profile',
                payload: {
                    person_1_birth_location: payload.birth_location,
                },
            });

            yield put({
                type: 'user:force-refresh',
                payload: {
                    profiles: true,
                },
            });
        }

        if (payload.privacy_policy) {
            yield all([
                put({
                    type: 'cookie:set',
                    payload: {
                        name: 'PRIVACY_POLICY_CONSENT',
                        value: 1,
                    },
                }),
                put({
                    type: 'cookie:set',
                    payload: {
                        name: 'POLICY_CONSENT_SHOWN',
                        value: 1,
                    },
                }),
                put({
                    type: 'member:privacy-policy-consent',
                }),
            ]);
        }
    } catch (e) {
        const error = get_error_messages(e.body.detail || null);
        const devcat = getDevcat();

        // Normalize error_type for GA so we don't dump full objects
        let errorType = 'unknown';
        if (error && error.phoneNumberError) {
            errorType = 'duplicate_phone';
        } else if (error && error.emailError) {
            errorType = 'email_error';
        } else if (error && error.passwordError) {
            errorType = 'password_error';
        } else if (error && error.birthDateError) {
            errorType = 'invalid_birth_date';
        } else if (error && error.generalError) {
            errorType = 'general_error';
        }

        yield put({
            type: 'ga:custom_event',
            payload: {
                event_name: 'sign_up_error',
                params: {
                    devcat: devcat,
                    ctx: ctx,
                    meth: meth,
                    error_type: JSON.stringify(errorType),
                },
            },
        });

        // If error is specifically a phone number error, do not route away; keep the user on step 4
        if (error.phoneNumberError) {
            yield all([
                put({
                    type: 'user:refresh',
                }),
                put({
                    type: 'user:update',
                    payload: {
                        ...error,
                        ...{
                            birth_date: payload.birth_date,
                            birth_name: payload.display_name,
                            submitting: false,
                        },
                    },
                }),
            ]);
        } else {
            // For signup errors (not phone), don't route away if signup happened at step 2
            // The signup modal will handle displaying errors on step 2
            // Only route away if this was a standalone signup (not in modal flow)
            const isModalSignup = payload && payload.signup_gateway === 'signup';

            const actions = [
                put({
                    type: 'user:update',
                    payload: {
                        ...error,
                        ...{
                            birth_date: payload.birth_date,
                            birth_name: payload.display_name,
                            submitting: false,
                        },
                    },
                }),
            ];

            // Only refresh and route away if this is NOT a modal signup (standalone signup)
            if (!isModalSignup) {
                actions.unshift(put({
                    type: 'user:refresh',
                }));
                actions.push(put({
                    type: 'route:to',
                    payload: error.emailError || error.passwordError ? 'login' : 'signup',
                }));
            }

            yield all(actions);
        }
    }
}
/* eslint-enable complexity */

const get_error_messages = (detail) => {
    let message = {};

    if (detail && detail.fields) {
        if (detail.fields.birth_date) {
            message.birthDateError = 'Please check your Birth Date.';
        }

        if (detail.fields.email) {
            message.emailError = detail.fields.email[0];
        }

        if (detail.fields.password) {
            message.passwordError = detail.fields.password[0];
        }

        if (detail.fields.phone_number) {
            // Capture backend phone number errors for step 4
            message.phoneNumberError = detail.fields.phone_number[0];
        }

        message.generalError = 'Please fill out required fields.';
    } else if (detail && detail.message) {
        message.generalError = detail.message;
    }

    return message;
};

export function* root() {
    yield all([
        takeEvery('user:signup-api', signupApi),
    ]);
}
